import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de «Mensajes directos».
 *
 * ## Los nueve endpoints NO cambian
 *
 * Los mismos que pedía `page-notifications.js`, con los mismos parámetros.
 * Lo vigila `test_mensajes_directos_pantalla.py`.
 *
 * ## El que se queda mal, y por qué
 *
 * `PUT /notifications/templates/{id}` recibe sus cuatro campos como **query
 * params** —así está declarada la firma del router
 * (`notifications.py:233`)—, incluido `body_html_template`, que es el cuerpo
 * HTML de un correo. `Convenciones.md` lo prohíbe por nombre y da los dos
 * motivos: un texto largo se corta con un 414 del proxy, y **queda escrito en
 * el log de accesos** de nginx (comprobado: el log registra la query string).
 *
 * Medido el 2026-09-15 con la plantilla más grande de la base: 937 caracteres
 * de HTML se convierten en 1.458 en la URL (×1,6). Hoy pasa; una plantilla con
 * una tabla no pasaría.
 *
 * **No se mueve aquí.** Cambiar la firma de un endpoint es un cambio de
 * contrato, no un rediseño, y el método lo dice sin matices. Va con su propio
 * cambio. Es el mismo trato que recibió `motivo` en Novedades, que sigue
 * viajando en la URL de `DELETE /posts/{id}` con esta misma nota.
 *
 * Su gemela `POST /notifications/templates` **sí manda un cuerpo** (`TemplateBody`),
 * y además valida el nombre; el `PUT` no valida nada.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Los cuatro grupos de destinatario que entiende `_resolve_recipients`. */
export type GrupoDeDestinatarios =
  | 'todos_activos' | 'por_area' | 'por_rol' | 'seleccion_manual';

/** Las etiquetas legibles. El vanilla enseñaba la CLAVE en la vista previa
 *  —«Destinatarios: todos_activos»—, que es la última pantalla que ve alguien
 *  antes de escribir a toda la empresa. */
export const ETIQUETA_DE_GRUPO: Record<GrupoDeDestinatarios, string> = {
  todos_activos: 'Todos los colaboradores activos',
  por_area: 'Por área',
  por_rol: 'Por rol',
  seleccion_manual: 'Selección manual',
};

/** Los tres roles que ofrece el selector. Son los del sistema; un rol a medida
 *  no aparece aquí porque el endpoint filtra por `User.role`. */
export const ROLES = [
  { clave: 'colaborador', etiqueta: 'Colaboradores' },
  { clave: 'coordinador', etiqueta: 'Coordinadores' },
  { clave: 'admin', etiqueta: 'Administradores' },
] as const;

export interface PersonaBuscada {
  id: number;
  full_name: string;
  area?: string | null;
  email?: string | null;
}

export interface Area {
  id: number;
  name: string;
}

export interface AdjuntoSubido {
  key: string;
  filename: string;
  content_type: string;
  size: number;
}

export interface EnvioDelHistorial {
  id: number;
  title: string;
  status: string;
  recipient_count: number;
  delivered: number;
  failed: number;
  sent_at?: string | null;
  created_at?: string | null;
}

export interface FilaDelReporte {
  full_name: string;
  email: string;
  email_sent: boolean;
  is_read: boolean;
}

export interface ReporteDeEnvio {
  id: number;
  title: string;
  message: string;
  status: string;
  sent_at?: string | null;
  total: number;
  delivered: number;
  failed: number;
  read: number;
  items: FilaDelReporte[];
}

export interface Plantilla {
  id: number;
  name: string;
  slug: string;
  subject_template: string;
  body_html_template: string;
  body_push_template?: string | null;
  is_system: boolean;
  is_editable: boolean;
}

export interface CuerpoDeNotificacion {
  title: string;
  message: string;
  recipient_group: GrupoDeDestinatarios;
  recipient_area_id: number | null;
  recipient_role: string | null;
  user_ids: number[] | null;
  channel_email: boolean;
  channel_push: boolean;
  channel_feed: boolean;
  body_is_html: boolean;
  attachments: AdjuntoSubido[];
  schedule_at: string | null;
}

export interface RespuestaDeEnvio {
  recipient_count: number;
  deferred_until?: string | null;
}

/** Cuántos envíos trae el historial. Era 30 en el vanilla y se conserva. */
export const POR_PAGINA = 30;

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  historial: ['notificaciones', 'historial'] as const,
  reporte: (id: number) => ['notificaciones', 'reporte', id] as const,
  plantillas: ['notificaciones', 'plantillas'] as const,
  destinatarios: (q: string) => ['comunicaciones', 'destinatarios', q] as const,
  // La misma clave que usan Directorio y Celebraciones: comparten la caché.
  areas: ['usuarios', 'areas'] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

export function useHistorial() {
  return useQuery({
    queryKey: claves.historial,
    queryFn: () => api.get<EnvioDelHistorial[]>('/notifications/history', { limit: POR_PAGINA }),
  });
}

export function useReporte(id: number | null) {
  return useQuery({
    queryKey: claves.reporte(id ?? 0),
    queryFn: () => api.get<ReporteDeEnvio>(`/notifications/${id}/report`),
    enabled: id != null,
  });
}

export function usePlantillas() {
  return useQuery({
    queryKey: claves.plantillas,
    queryFn: () => api.get<Plantilla[]>('/notifications/templates'),
  });
}

/** Las áreas, solo cuando el grupo elegido las necesita.
 *
 *  `enabled` importa: sin él, abrir el compositor pediría las áreas siempre,
 *  y «Por área» es uno de cuatro grupos. Comparte clave con Directorio y
 *  Celebraciones, así que si alguien ya pasó por ahí no se pide nada. */
export function useAreas(activa: boolean) {
  return useQuery({
    queryKey: claves.areas,
    queryFn: () => api.get<Area[]>('/users/areas'),
    enabled: activa,
    staleTime: 10 * 60_000,
  });
}

/** El buscador de destinatarios, con la misma clave que usa Comunicaciones:
 *  las dos pantallas comparten caché sobre el mismo endpoint. */
export function useBuscarDestinatarios(q: string, activa: boolean) {
  return useQuery({
    queryKey: claves.destinatarios(q.trim()),
    queryFn: () =>
      api.get<PersonaBuscada[]>('/notifications/recipients/search', { q: q.trim(), limit: 30 }),
    enabled: activa,
    placeholderData: (previa) => previa,
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

export function useSubirAdjunto() {
  return useMutation({
    mutationFn: (archivo: File) => {
      const datos = new FormData();
      datos.append('file', archivo);
      return api.upload<AdjuntoSubido>('/notifications/attachments', datos);
    },
  });
}

export function useEnviar() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: CuerpoDeNotificacion) =>
      api.post<RespuestaDeEnvio>('/notifications/send', cuerpo),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.historial }),
  });
}

export function useCrearPlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cuerpo: {
      name: string; subject_template: string;
      body_html_template: string; body_push_template: string;
    }) => api.post<{ id: number; slug: string }>('/notifications/templates', cuerpo),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.plantillas }),
  });
}

/** Guardar una plantilla existente.
 *
 *  Los cuatro campos viajan en la URL porque así lo declara el endpoint hoy
 *  (`notifications.py:233`, parámetros sueltos en la firma = query params).
 *  **Es el defecto declarado arriba, y se conserva a propósito**: moverlo al
 *  cuerpo cambia el contrato del servidor y eso va con su propio cambio. */
export function useGuardarPlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...campos }: {
      id: number; name: string; subject_template: string;
      body_html_template: string; body_push_template: string;
    }) => {
      const params = new URLSearchParams(campos);
      return api.put<{ message: string }>(`/notifications/templates/${id}?${params}`, null);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.plantillas }),
  });
}

export function useBorrarPlantilla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del<{ message: string }>(`/notifications/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.plantillas }),
  });
}
